import React, { useEffect, useState } from "react";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  closestCenter,
  pointerWithin,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import PageHeader from "../components/PageHeader";
import Help from "../components/Help";
import { helpPagesData } from "../utils/helper";
import { NavLink, useNavigate } from "react-router-dom";

import trash from "../assets/red-trash.svg";
import textIcon from "../assets/text.svg";
import paragraphIcon from "../assets/paragraph.svg";
import buttonIcon from "../assets/button.svg";
import uploadImageIcon from "../assets/uploadImage.svg";
import tableIcon from "../assets/table.svg";
import urlIcon from "../assets/url.svg";
import subtextIcon from "../assets/subtext.svg";
import videoIcon from "../assets/video.svg";
import emptysection from "../assets/empty-section.svg";
import sortIcon from "../assets/draghandle.svg";
import undoIcon from "../assets/undo.svg";
import { saveTemplate, StandardFieldList } from "../action/sectiondata";
import dropdownIcon from "../assets/dropdown-icon.svg";
import SwitchToggle from "../components/switch";
import { requestWithToken } from "../utils/apis"; // used for save API call
import { useToast } from "../components/ToastProvider";
import { Spinner } from "react-bootstrap";

// fallback icons map
const ICON_MAP = {
  text: textIcon,
  textarea: paragraphIcon,
  button: buttonIcon,
  image: uploadImageIcon,
  table: tableIcon,
  url: urlIcon,
  subtext: subtextIcon,
  video: videoIcon,
};

/* ---------------------------
   Draggable item (left library)
----------------------------*/
function DraggableField({
  id,
  label,
  icon,
  payloadType,
  placeholderEnabled,
  standard_field_id,
}) {
  const { listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    data: {
      id,
      label,
      icon,
      type: payloadType,
      source: "library",
      placeholderEnabled,
      standard_field_id,
    },
  });

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    boxShadow: isDragging
      ? "-19px 10px 13px 0px #0000000D, -34px 18px 15px 0px #00000003, -53px 28px 17px 0px #00000000"
      : undefined,
    background: isDragging ? "#fff" : undefined,
    zIndex: isDragging ? 999 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      className="field-item"
      onDoubleClick={(e) => e.preventDefault()}
      title={label}
    >
      <span className="field-label">{label}</span>
      {icon && <img src={icon} alt={label} className="draggable-icon" />}
    </div>
  );
}

/* ---------------------------
   SortableField component
----------------------------*/
function SortableField({
  item,
  onLabelChange,
  onDelete,
  isExpanded,
  toggleExpand,
  onToggleChange,
  isMulti,
  index,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.95 : 1,
    zIndex: isDragging ? 999 : "auto",
    background: isDragging ? "#fff" : undefined,
    boxShadow: isDragging
      ? "-19px 10px 13px 0px #0000000D, -34px 18px 15px 0px #00000003, -53px 28px 17px 0px #00000000"
      : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className="field-name-wrapper">
      <img
        ref={setActivatorNodeRef}
        {...listeners}
        className="field-sort-btn"
        src={sortIcon}
        alt="move"
      />

      <div className="field-item dropped">
        <div className="dropped-header">
          <div className="dropped-left">
            {item.icon && (
              <img
                src={item.icon}
                alt={item.label}
                className="dropped-icon"
              />
            )}
            <span className="dropped-label">{item.label}</span>
          </div>

          <div className="dropped-right">
            <button
              className="delete-field-btn"
              onClick={() => onDelete(item.id)}
              type="button"
            >
              Delete
            </button>

            <button
              className="expand-btn"
              onClick={() => toggleExpand(item.id)}
              type="button"
            >
              <img
                src={dropdownIcon}
                alt="toggle"
                className={`dropdown-arrow ${isExpanded ? "open" : ""}`}
              />
            </button>
          </div>
        </div>

        {isExpanded && (
          <div className="dropped-body">
            <div className="dropped-fields">
              <input
                type="text"
                placeholder="Enter Field label or title"
                value={item.labelText}
                onChange={(e) =>
                  onLabelChange(item.id, e.target.value, "labelText")
                }
              />
              {item.__errors?.labelText && (
                <div className="input-error">{item.__errors.labelText}</div>
              )}

              {item.placeholderEnabled && (
                <>
                  <input
                    type="text"
                    placeholder="Enter Placeholder"
                    value={item.placeholder || ""}
                    onChange={(e) =>
                      onLabelChange(item.id, e.target.value, "placeholder")
                    }
                  />
                  {item.__errors?.placeholder && (
                    <div className="input-error">
                      {item.__errors.placeholder}
                    </div>
                  )}
                </>
              )}

              {isMulti && (
                <div className="switch-btn">
                  Enable this if the input will appear multiple times.
                  <SwitchToggle
                    checked={!!item.toggle}
                    onChange={(e) =>
                      onToggleChange(item.id, e.target.checked)
                    }
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* -----------------------------------------
  EmptyBox UI
----------------------------------------- */
const EmptyBox = ({ title, desc }) => (
  <div className="empt-state">
    <img src={emptysection} alt="empty" />
    <h6 className="text-center">{title}</h6>
    <p className="text-muted text-center">{desc}</p>
  </div>
);

/* -----------------------------------------
  Droppable Fixed
----------------------------------------- */
const DroppableFixed = ({
  fields,
  onLabelChange,
  onDelete,
  expandedSet,
  toggleExpand,
  onToggleChange,
}) => {
  const { setNodeRef, isOver } = useDroppable({ id: "fixed-area" });

  return (
    <div ref={setNodeRef} className={`drop-box ${isOver ? "over" : ""}`}>
      <h6>Fixed Fields</h6>
      {fields.length === 0 ? (
        <EmptyBox
          title="Drop Fixed Fields Here"
          desc="Add single-instance fields like title or subtitle that appear once per section."
        />
      ) : (
        <SortableContext
          items={fields.map((f) => f.id)}
          strategy={verticalListSortingStrategy}
        >
          {fields.map((item, idx) => (
            <SortableField
              key={item.id}
              item={item}
              onLabelChange={onLabelChange}
              onDelete={onDelete}
              isExpanded={expandedSet.has(item.id)}
              toggleExpand={toggleExpand}
              onToggleChange={onToggleChange}
              index={idx}
            />
          ))}
        </SortableContext>
      )}
    </div>
  );
};

/* -----------------------------------------
  Droppable Multi
----------------------------------------- */
const DroppableMulti = ({
  fields,
  onLabelChange,
  onDelete,
  expandedSet,
  toggleExpand,
  onToggleChange,
}) => {
  const { setNodeRef, isOver } = useDroppable({ id: "multi-area" });

  return (
    <div ref={setNodeRef} className={`drop-box ${isOver ? "over" : ""}`}>
      <h6>Fields for Multiple Instance</h6>

      {fields.length === 0 ? (
        <EmptyBox
          title="Drop Fields for Multiple Entries"
          desc="Add input fields that generate repeatable sets such as testimonials or FAQs."
        />
      ) : (
        <SortableContext
          items={fields.map((f) => f.id)}
          strategy={verticalListSortingStrategy}
        >
          {fields.map((item, idx) => (
            <SortableField
              key={item.id}
              item={item}
              onLabelChange={onLabelChange}
              onDelete={onDelete}
              isExpanded={expandedSet.has(item.id)}
              toggleExpand={toggleExpand}
              onToggleChange={onToggleChange}
              index={idx}
              isMulti={true}
            />
          ))}
        </SortableContext>
      )}
    </div>
  );
};

/* -----------------------------------------
  MAIN Component
----------------------------------------- */
const AddTemplate = () => {
  const navigate = useNavigate();

  const [templateName, setTemplateName] = useState("");
  const [fixedFields, setFixedFields] = useState([]);
  const [multiFields, setMultiFields] = useState([]);
  const [backup, setBackup] = useState({ fixed: [], multi: [] });
  const [library, setLibrary] = useState([]);
  const [libLoading, setLibLoading] = useState(false);
  const [libError, setLibError] = useState(null);
  const [expandedSet, setExpandedSet] = useState(new Set());
  const [errors, setErrors] = useState({ templateName: null });
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const { showToast } = useToast();

  /* -----------------------------------------
     Load Standard Fields from API
  ----------------------------------------- */
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10, // Only drag when moved by 10px
      },
    })
  );

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLibLoading(true);
      try {
        const res = await StandardFieldList();
        if (mounted) {
          if (res?.status === 1) {
            const list = res.data?.list || [];
            const normalized = list.map((it) => ({
              id: it._id,
              label: it.title,
              type: it.type,
              required: it.required ?? false,
              placeholderEnabled: it.placeholder ?? false,
              icon: it.icon
                ? `${process.env.REACT_APP_BUCKET_URL}${it.icon}`
                : ICON_MAP[it.type] || null,
              standard_field_id: it._id,
            }));
            setLibrary(normalized);
            setLibError(null);
          } else {
            setLibrary([]);
            setLibError(res?.message || "Failed to load fields");
          }
        }
      } catch (err) {
        if (mounted) setLibError("Failed to load fields");
      } finally {
        if (mounted) setLibLoading(false);
      }
    };
    load();
    return () => (mounted = false);
  }, []);

  // DND: handle drag end and reorder
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;

    // --------------------------
    // FROM LIBRARY → decide zone
    // --------------------------
    if (active.data.current?.source === "library") {
      const uniqueId = `${active.id}-${Date.now()}`;
      const source = active.data.current;

      const newField = {
        id: uniqueId,
        type: source.type,
        label: source.label,
        standard_field_id: source.standard_field_id,
        icon: source.icon, // full URL already
        required: source.required ?? false,
        placeholderEnabled: source.placeholderEnabled ?? false,
        labelText: "",
        placeholder: source.placeholderEnabled ? "" : null,
        toggle: false,
        __errors: {},
      };

      const overId = over.id;

      // ⭐ Decide which list based on where it was dropped:
      let targetList = null;
      if (
        overId === "fixed-area" ||
        fixedFields.some((f) => f.id === overId)
      ) {
        targetList = "fixed";
      } else if (
        overId === "multi-area" ||
        multiFields.some((f) => f.id === overId)
      ) {
        targetList = "multi";
      } else {
        // dropped somewhere irrelevant
        return;
      }

      setBackup({ fixed: fixedFields, multi: multiFields });

      if (targetList === "fixed") {
        setFixedFields((prev) => [...prev, newField]); // always append
      } else {
        setMultiFields((prev) => [...prev, newField]); // always append
      }

      return;
    }

    // reordering inside fixedFields
    if (fixedFields.some((f) => f.id === active.id)) {
      const oldIndex = fixedFields.findIndex((f) => f.id === active.id);
      const newIndex = fixedFields.findIndex((f) => f.id === over.id);
      if (newIndex !== -1 && oldIndex !== newIndex) {
        setBackup({ fixed: fixedFields, multi: multiFields });
        setFixedFields((prev) => arrayMove(prev, oldIndex, newIndex));
      }
      return;
    }

    // reordering inside multiFields
    if (multiFields.some((f) => f.id === active.id)) {
      const oldIndex = multiFields.findIndex((f) => f.id === active.id);
      const newIndex = multiFields.findIndex((f) => f.id === over.id);
      if (newIndex !== -1 && oldIndex !== newIndex) {
        setBackup({ fixed: fixedFields, multi: multiFields });
        setMultiFields((prev) => arrayMove(prev, oldIndex, newIndex));
      }
      return;
    }
  };

  // Expand / Collapse logic
  const toggleExpand = (id) => {
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Field updates & delete & toggle changes
  const handleFixedLabelChange = (id, value, key = "labelText") => {
    setFixedFields((prev) =>
      prev.map((i) =>
        i.id === id
          ? {
            ...i,
            [key]: value,
            __errors: { ...i.__errors, [key]: null },
          }
          : i
      )
    );
  };

  const handleMultiLabelChange = (id, value, key = "labelText") => {
    setMultiFields((prev) =>
      prev.map((i) =>
        i.id === id
          ? {
            ...i,
            [key]: value,
            __errors: { ...i.__errors, [key]: null },
          }
          : i
      )
    );
  };

  const handleFixedDelete = (id) => {
    setBackup({ fixed: fixedFields, multi: multiFields });
    setFixedFields((prev) => prev.filter((i) => i.id !== id));
    setExpandedSet((s) => {
      const next = new Set(s);
      next.delete(id);
      return next;
    });
  };

  const handleMultiDelete = (id) => {
    setBackup({ fixed: fixedFields, multi: multiFields });
    setMultiFields((prev) => prev.filter((i) => i.id !== id));
    setExpandedSet((s) => {
      const next = new Set(s);
      next.delete(id);
      return next;
    });
  };

  // persist toggle into item state
  const handleToggleChangeFixed = (id, checked) => {
    setFixedFields((prev) =>
      prev.map((i) => (i.id === id ? { ...i, toggle: checked } : i))
    );
  };
  const handleToggleChangeMulti = (id, checked) => {
    setMultiFields((prev) =>
      prev.map((i) => (i.id === id ? { ...i, toggle: checked } : i))
    );
  };

  // undo
  const handleUndo = () => {
    setFixedFields(backup.fixed || []);
    setMultiFields(backup.multi || []);
    setExpandedSet(new Set());
  };

  //  Validation before Save
  const validateAll = () => {
    let ok = true;
    const newErrors = { templateName: null };

    if (!templateName || !templateName.trim()) {
      newErrors.templateName = "Template Name is required";
      ok = false;
    }

    const fixed = fixedFields.map((f) => {
      const errs = {};
      if (!f.labelText || !f.labelText.trim()) {
        errs.labelText = "Field label is required";
        ok = false;
      }
      return { ...f, __errors: errs };
    });

    const multi = multiFields.map((f) => {
      const errs = {};
      if (!f.labelText || !f.labelText.trim()) {
        errs.labelText = "Field label is required";
        ok = false;
      }
      return { ...f, __errors: errs };
    });

    setFixedFields(fixed);
    setMultiFields(multi);
    setErrors(newErrors);

    return ok;
  };

  const buildPayload = () => {
    const fixedPayload = fixedFields.map((f) => ({
      label: f.labelText ? f.labelText.trim() : f.label,
      type: f.type,
      required: !!f.required,
      placeholder: f.placeholderEnabled ? f.placeholder || "" : "",
      standard_field: f.standard_field_id || null,
    }));

    const multiArr = [];

    for (const f of multiFields) {
      const base = {
        label: f.labelText ? f.labelText.trim() : f.label,
        type: f.type,
        required: !!f.required,
        placeholder: f.placeholderEnabled ? f.placeholder || "" : "",
        standard_field: f.standard_field_id || null,
      };

      if (f.toggle) {
        const nested = {
          label: base.label,
          type: "repeater",
          fields: [
            {
              label: base.label,
              type: base.type,
              required: base.required,
              placeholder: base.placeholder,
              standard_field: f.standard_field_id || null,
            },
          ],
        };
        multiArr.push(nested);
      } else {
        multiArr.push(base);
      }
    }

    const payload = {
      label: templateName,
      type: templateName.trim().toLowerCase().replace(/\s+/g, "-"),
      fields: [...fixedPayload],
    };

    if (multiArr.length > 0) {
      payload.fields.push({
        label: "Cards",
        type: "repeater",
        fields: multiArr,
      });
    }

    return payload;
  };

  const handleSave = async () => {
    setApiError("");
    if (!validateAll()) {
      // open errored items
      const toOpen = new Set();
      fixedFields.forEach((f) => {
        if (f.__errors?.labelText) toOpen.add(f.id);
      });
      multiFields.forEach((f) => {
        if (f.__errors?.labelText) toOpen.add(f.id);
      });
      setExpandedSet(
        (prev) =>
          new Set([...Array.from(prev), ...Array.from(toOpen)])
      );
      return;
    }

    const payload = buildPayload();
    setLoading(true);

    try {
      const res = await saveTemplate(payload);
      if (res?.status === 1) {
        showToast("Template saved successfully", "success");
        navigate("/section");
        return;
      }
      setApiError(res?.message || "Failed to save template");
      showToast(res?.message || "Failed to save template", "error");
    } catch (err) {
      setApiError("Something went wrong");
      showToast("Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader pageTitle="Section Masters">
    
          <Help
            title={helpPagesData.pages.title}
            message={helpPagesData.pages.message}
            items={helpPagesData.pages.items}
          />
          <button className="btn-black" onClick={handleSave} disabled={loading}>
            {loading ? "Saving..." : "Save"}
          </button>

      </PageHeader>

      <div className="page-container">
        <DndContext
          sensors={sensors} 
          collisionDetection={pointerWithin}
          onDragEnd={handleDragEnd}
        >
          <div className="row">
            {/* LEFT */}
            <div className="col-md-5">
              <div className="theme-card mb-3 p-3">
                <div className="card-title mb-3">Template Details</div>
                <label className="item-label">Name Template</label>
                <input
                  type="text"
                  placeholder="Enter Template Name"
                  className="page-menu-item"
                  value={templateName}
                  onChange={(e) => {
                    setTemplateName(e.target.value);
                    setErrors((prev) => ({ ...prev, templateName: null }));
                  }}
                />
                {errors.templateName && (
                  <div className="input-error">{errors.templateName}</div>
                )}
                {apiError && (
                  <div className="input-error mt-2">{apiError}</div>
                )}
              </div>

              <div className="theme-card p-3">
                <div className="card-title mb-3">Standard Fields</div>

                {libLoading ? (
                  <div className="center-div">
                    <Spinner size="md" />
                  </div>
                ) : libError ? (
                  <div className="input-error">Failed to load fields</div>
                ) : (
                  <div className="draggable-fields">
                    {library.map((f) => (
                      <DraggableField
                        key={f.id}
                        id={f.id}
                        label={f.label}
                        icon={f.icon}
                        payloadType={f.type}
                        placeholderEnabled={f.placeholderEnabled}
                        standard_field_id={f.standard_field_id}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT */}
            <div className="col-md-7">
              <div className="theme-card p-4">
                <div className="card-title mb-4 d-flex justify-content-between align-items-center">
                  <div>Section Builder</div>

                  {(fixedFields.length > 0 || multiFields.length > 0) && (
                    <button className="undo-btn" onClick={handleUndo}>
                      <img src={undoIcon} alt="undo" /> Undo Changes
                    </button>
                  )}
                </div>

                <DroppableFixed
                  fields={fixedFields}
                  onLabelChange={handleFixedLabelChange}
                  onDelete={handleFixedDelete}
                  expandedSet={expandedSet}
                  toggleExpand={toggleExpand}
                  onToggleChange={handleToggleChangeFixed}
                />

                <DroppableMulti
                  fields={multiFields}
                  onLabelChange={handleMultiLabelChange}
                  onDelete={handleMultiDelete}
                  expandedSet={expandedSet}
                  toggleExpand={toggleExpand}
                  onToggleChange={handleToggleChangeMulti}
                />
              </div>
            </div>
          </div>
        </DndContext>
      </div>
    </div>
  );
};

export default AddTemplate;
